import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import FacilityBuildingIllustration from '@/assets/svg/facility-building-illustration.svg';
import { SearchBar } from '@/shared/components/SearchBar';
import { FilterBar } from '@/shared/components/FilterBar';
import { FilterPopup } from '@/shared/components/FilterPopup';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import { FACILITY_CREATE_ERROR_MESSAGES } from '../../errors/errorMessages';
import { FacilityCard } from '../../components/FacilityCard/FacilityCard';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import type { FacilityListItem } from '../../types/facilityListType';
import { useFacilityListPage } from '../../hooks/useFacilityListPage';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="relative overflow-hidden bg-[#E8EEF0]/8 border border-[#e8eef0] rounded-xl h-[263px] animate-pulse">
    <div
      className="absolute top-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[#e8eef0]"
      style={{ left: '-160px' }}
    />
    <div
      className="absolute inset-y-0 right-0 flex flex-col justify-between py-[23px]"
      style={{ left: '163px', paddingRight: '40px' }}
    >
      <div className="flex flex-col gap-2 pr-8">
        <div className="h-5 bg-[#e8eef0] rounded w-3/4" />
        <div className="h-3 bg-[#e8eef0] rounded w-2/5" />
        <div className="h-3 bg-[#e8eef0] rounded w-1/3" />
      </div>
      <div className="h-6 bg-[#e8eef0] rounded w-20" />
      <div className="flex flex-col gap-3">
        <div className="h-4 bg-[#e8eef0] rounded w-2/3" />
        <div className="h-4 bg-[#e8eef0] rounded w-1/2" />
        <div className="h-4 bg-[#e8eef0] rounded w-3/4" />
      </div>
    </div>
  </div>
);

// ─── FacilityListPage ─────────────────────────────────────────────────────────

export default function FacilityListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    facilities,
    loading,
    loadingMore,
    error,
    isNoFilterFetch,
    search,
    setSearch,
    isSearchClearing,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    hasNextPage,
    loadMore,
    isAccessDenied,
    canCreateFacility,
    hasCreateFacilityPermission,
    isAtFacilityLimit,
    maxFacilityLimit,
  } = useFacilityListPage();

  const { privileges } = useUserPrivileges();

  // ── Filter popup ──────────────────────────────────────────────────────────
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);

  const activeFilterCount = appliedFilters.statusOptions.filter(
    (o) => o.checked
  ).length;

  const handleCloseFilter = useCallback(() => {
    setStatusOptions(appliedFilters.statusOptions);
    setIsFilterOpen(false);
  }, [appliedFilters, setStatusOptions]);

  const handleApplyAndClose = useCallback(() => {
    handleApplyFilters();
    setIsFilterOpen(false);
  }, [handleApplyFilters]);

  const handleClearAndClose = useCallback(() => {
    handleClearFilters();
    setIsFilterOpen(false);
  }, [handleClearFilters]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isFilterOpen, handleCloseFilter]);

  // ── Infinite scroll ───────────────────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !loading && !loadingMore) {
          loadMore();
        }
      },
      { root: scrollContainer, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, loading, loadingMore, loadMore]);

  // ── Snackbar ──────────────────────────────────────────────────────────────
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (location.state?.successMessage) {
      setSnackbarMessage(location.state.successMessage as string);
      setShowSnackbar(true);
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [location, navigate]);

  // ── Derived states ────────────────────────────────────────────────────────
  const hasActiveSearch = search.trim().length > 0;
  const hasActiveFilters = activeFilterCount > 0;
  const hasNoData =
    !loading &&
    !error &&
    isNoFilterFetch &&
    facilities.length === 0 &&
    !hasActiveSearch;
  const hasNoResults =
    !loading &&
    !error &&
    !isSearchClearing &&
    facilities.length === 0 &&
    !hasNoData;
  const showControls = !hasNoData;

  // ── Menu action ───────────────────────────────────────────────────────────
  const handleMenuAction = useCallback(
    (action: string, facility: FacilityListItem) => {
      if (action === 'VIEW') {
        navigate(`/Placówki/${facility.id}/Podgląd-placówki`);
      } else if (action === 'EDIT') {
        navigate(`/Placówki/${facility.id}/Edycja-placówki`);
      }
    },
    [navigate]
  );

  if (isAccessDenied) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="text-[#181A1B] text-[22px] font-normal leading-7">
          {FACILITY_LABELS.FACILITY_TITLE}
        </div>
        {!hasNoData &&
          (canCreateFacility ? (
            <button
              onClick={() => navigate('/Placówki/Dodaj-placówkę')}
              data-testid="Dodaj placówkę-button"
              className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                Dodaj placówkę
              </span>
            </button>
          ) : loading ? (
            // Still loading — show disabled button without tooltip to avoid
            // misleading "no permission" message before data has resolved.
            <button
              disabled
              className="h-10 bg-[#2A4455] opacity-40 cursor-not-allowed px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                Dodaj placówkę
              </span>
            </button>
          ) : (
            <Tooltip
              content={
                !hasCreateFacilityPermission
                  ? FACILITY_CREATE_ERROR_MESSAGES.ACCESS_DENIED_TOOLTIP
                  : isAtFacilityLimit && maxFacilityLimit !== null
                    ? FACILITY_CREATE_ERROR_MESSAGES.LIMIT_REACHED_TOOLTIP(
                        maxFacilityLimit
                      )
                    : FACILITY_CREATE_ERROR_MESSAGES.ACCESS_DENIED_TOOLTIP
              }
              forceShow
              side="bottom"
              contentMaxWidth={320}
              triggerWrapContent
            >
              <button
                disabled
                className="h-10 bg-[#2A4455] opacity-40 cursor-not-allowed px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  Dodaj placówkę
                </span>
              </button>
            </Tooltip>
          ))}
      </div>

      {/* Search + Filter */}
      {showControls && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Szukaj po nazwie"
          />
          <div ref={filterContainerRef} className="relative">
            <FilterBar
              onClick={() => setIsFilterOpen((prev) => !prev)}
              isOpen={isFilterOpen}
              filterCount={activeFilterCount}
            />
            <FilterPopup
              isOpen={isFilterOpen}
              onClose={handleCloseFilter}
              statusOptions={statusOptions}
              onStatusChange={setStatusOptions}
              showDateRange={false}
              onApply={handleApplyAndClose}
              onClear={handleClearAndClose}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
      >
        {error && (
          <p className="text-sm text-[#E11B1B] leading-5 tracking-[0.25px]">
            {error}
          </p>
        )}

        {loading || isSearchClearing ? (
          /* ── Initial / reset skeleton ── */
          <div className="grid grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-x-10 gap-y-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : hasNoData ? (
          /* ── No data: no facilities exist yet ── */
          <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
            <LottieAnimation
              animationData={noDataAnimation}
              width={277}
              aspectRatio="1 / 1"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                Brak placówek.
              </p>
              <p className="text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                Placówki pojawią się tutaj po dodaniu ich do systemu.
              </p>
            </div>
          </div>
        ) : hasNoResults ? (
          /* ── No results: search/filter returned nothing ── */
          <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                Nie znaleziono wyników.
              </p>
              <p className="text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                {hasActiveSearch && hasActiveFilters
                  ? 'Spróbuj zmienić kryteria wyszukiwania lub filtry.'
                  : hasActiveSearch
                    ? 'Spróbuj zmienić kryteria wyszukiwania.'
                    : 'Spróbuj zmienić ustawienia filtrów.'}
              </p>
            </div>
          </div>
        ) : (
          /* ── Facility cards ── */
          <div className="grid grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-x-10 gap-y-8">
            {facilities.map((facility, index) => (
              <FacilityCard
                key={facility.id}
                facility={facility}
                illustration={FacilityBuildingIllustration}
                onMenuAction={handleMenuAction}
                canEdit={privileges.canEditFacility}
                index={index}
              />
            ))}

            {/* Load-more skeleton rows appended below existing cards */}
            {loadingMore &&
              Array.from({ length: 2 }).map((_, i) => (
                <SkeletonCard key={`more-${i}`} />
              ))}

            {/* Sentinel — observed by IntersectionObserver to trigger loadMore */}
            <div
              ref={sentinelRef}
              className="col-span-full h-1"
              aria-hidden="true"
            />
          </div>
        )}
      </div>

      <Snackbar
        open={showSnackbar}
        message={snackbarMessage}
        variant="success"
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </div>
  );
}
